/* Focal Video Carousel — looping, centered focal slide, both-side peeks */
if (!customElements.get('focal-video-carousel')) {
  class FocalVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.fvc__track');
      this.viewport = this.querySelector('.fvc__viewport');
      this.dots = Array.from(this.querySelectorAll('.fvc__dot'));
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.autoAdvance = this.dataset.autoAdvance === 'true';
      this.autoAdvanceInterval = (parseInt(this.dataset.autoAdvanceInterval, 10) || 5) * 1000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.realCount = parseInt(this.dataset.count, 10) || 0;
      this.index = 0;              // logical index into the REAL slides
      this._autoTimer = null;
      this._onResize = this._debounce(() => this.layout(false), 150);
    }

    connectedCallback() {
      if (!this.track || this.realCount === 0) return;
      this.originals = Array.from(this.querySelectorAll('.fvc__slide'));

      // For looping, clone one slide on each end so a peek always exists.
      if (this.loop && this.realCount > 1) {
        const first = this.originals[0].cloneNode(true);
        const last = this.originals[this.originals.length - 1].cloneNode(true);
        first.classList.add('fvc__clone');
        last.classList.add('fvc__clone');
        first.classList.remove('is-active');
        last.classList.remove('is-active');
        this.track.appendChild(first);       // clone of first goes at the end
        this.track.insertBefore(last, this.originals[0]); // clone of last goes at the start
      }

      this.slides = Array.from(this.querySelectorAll('.fvc__slide'));
      // offset: with a leading clone, real slide 0 is at DOM position 1
      this.domOffset = (this.loop && this.realCount > 1) ? 1 : 0;

      // Dots
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10)));
      });

      // Arrows
      const prev = this.querySelector('.fvc__arrow--prev');
      const next = this.querySelector('.fvc__arrow--next');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      // Click a peek slide to focus it
      this.slides.forEach((slide) => {
        slide.addEventListener('click', (e) => {
          if (slide.classList.contains('is-active')) return;
          if (e.target.closest('.fvc__discover')) return;
          e.preventDefault();
          const domIdx = this.slides.indexOf(slide);
          const realIdx = this._domToReal(domIdx);
          if (realIdx !== null) this.goTo(realIdx);
        });
      });

      window.addEventListener('resize', this._onResize);
      this.track.addEventListener('transitionend', () => this._afterTransition());

      // Auto-advance timer
      if (this.autoAdvance && this.realCount > 1) {
        this._startAuto();
        if (this.pauseOnHover) {
          this.addEventListener('mouseenter', () => this._stopAuto());
          this.addEventListener('mouseleave', () => this._startAuto());
        }
        // Pause when the tab is hidden, resume when visible
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this._stopAuto();
          else this._startAuto();
        });
      }

      requestAnimationFrame(() => this.layout(false));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._stopAuto();
    }

    _startAuto() {
      if (!this.autoAdvance || this.realCount <= 1) return;
      this._stopAuto();
      this._autoTimer = setInterval(() => {
        this.goTo(this.index + 1);
      }, this.autoAdvanceInterval);
    }

    _stopAuto() {
      if (this._autoTimer) {
        clearInterval(this._autoTimer);
        this._autoTimer = null;
      }
    }

    _restartAuto() {
      // Called after manual navigation so the timer resets from the new slide
      if (this.autoAdvance) this._startAuto();
    }

    _domToReal(domIdx) {
      const slide = this.slides[domIdx];
      if (!slide) return null;
      // clones carry the original data-index
      return parseInt(slide.dataset.index, 10);
    }

    goTo(index) {
      // Restart the auto-advance countdown from this navigation
      if (this.autoAdvance && this._autoTimer) this._startAuto();
      if (this.loop && this.realCount > 1) {
        // allow going one past either end into the clone, then snap
        this.index = index;
        this.layout(true);
      } else {
        if (index < 0) index = 0;
        if (index > this.realCount - 1) index = this.realCount - 1;
        this.index = index;
        this.layout(true);
      }
    }

    _currentDomIndex() {
      // logical index -> DOM position (accounting for leading clone)
      return this.index + this.domOffset;
    }

    layout(animate) {
      const domIdx = this._currentDomIndex();
      const active = this.slides[domIdx];
      if (!active) return;

      this.track.style.transition = animate ? '' : 'none';

      const viewportWidth = this.viewport.clientWidth;
      const slideWidth = active.offsetWidth;
      const slideLeft = active.offsetLeft;
      const offset = slideLeft - (viewportWidth - slideWidth) / 2;
      this.track.style.transform = `translateX(${-offset}px)`;

      if (!animate) {
        // force reflow so the 'none' transition applies immediately
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }

      this.slides.forEach((slide, i) => {
        const isActive = i === domIdx;
        slide.classList.toggle('is-active', isActive);
        const link = slide.querySelector('.fvc__discover');
        if (link) link.tabIndex = isActive ? 0 : -1;
      });

      // Dots reflect the real index (normalized)
      let realIndex = this.index;
      if (this.loop && this.realCount > 1) {
        realIndex = ((this.index % this.realCount) + this.realCount) % this.realCount;
      }
      this.dots.forEach((dot, i) => {
        const isActive = i === realIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this._updateVideos(domIdx);
    }

    _afterTransition() {
      if (!this.loop || this.realCount <= 1) return;
      // If we've moved onto a clone (index out of real range), snap back silently
      if (this.index < 0) {
        this.index = this.realCount - 1;
        this.layout(false);
      } else if (this.index > this.realCount - 1) {
        this.index = 0;
        this.layout(false);
      }
    }

    _updateVideos(domIdx) {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('[data-lazy-video]');
        if (!video) return;
        if (i === domIdx) {
          const source = video.querySelector('source[data-src]');
          if (source) {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
            video.load();
          }
          if (this.autoplay) {
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    _debounce(fn, wait) {
      let t;
      return () => { clearTimeout(t); t = setTimeout(fn, wait); };
    }
  }
  customElements.define('focal-video-carousel', FocalVideoCarousel);
}